import { supabase } from '@/lib/supabase';

export interface AccountBalance {
  id: string;
  userId: string;
  accountId: string;
  accountName: string;
  accountType: 'checking' | 'savings' | 'credit_card' | 'loan' | 'business' | 'tax' | 'emergency';
  ownership: 'personal' | 'business';
  businessName: string | null;
  balance: number;
  lastUpdated: string;
  createdAt: string;
  updatedAt: string;
}

export interface AccountBalanceRow {
  id: string;
  user_id: string;
  account_id: string;
  account_name: string;
  account_type: string;
  ownership: string;
  business_name: string | null;
  balance: number;
  last_updated: string;
  created_at: string;
  updated_at: string;
}

function rowToBalance(row: AccountBalanceRow): AccountBalance {
  return {
    id: row.id,
    userId: row.user_id,
    accountId: row.account_id,
    accountName: row.account_name,
    accountType: row.account_type as AccountBalance['accountType'],
    ownership: row.ownership as 'personal' | 'business',
    businessName: row.business_name,
    balance: Number(row.balance),
    lastUpdated: row.last_updated,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export async function getAccountBalances(userId: string): Promise<AccountBalance[]> {
  const { data, error } = await supabase
    .from('account_balances')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data || []).map(rowToBalance);
}

export async function upsertAccountBalance(
  userId: string,
  accountId: string,
  balance: number
): Promise<AccountBalance> {
  const { data, error } = await supabase
    .from('account_balances')
    .update({ balance, last_updated: new Date().toISOString() })
    .eq('user_id', userId)
    .eq('account_id', accountId)
    .select()
    .single();

  if (error) throw error;
  return rowToBalance(data);
}

export async function upsertAccountBalanceFull(
  userId: string,
  payload: {
    accountId: string;
    accountName: string;
    accountType: AccountBalance['accountType'];
    ownership: 'personal' | 'business';
    businessName: string | null;
    balance: number;
  }
): Promise<AccountBalance> {
  const { data, error } = await supabase
    .from('account_balances')
    .upsert(
      {
        user_id: userId,
        account_id: payload.accountId,
        account_name: payload.accountName,
        account_type: payload.accountType,
        ownership: payload.ownership,
        business_name: payload.businessName,
        balance: payload.balance,
        last_updated: new Date().toISOString(),
      },
      { onConflict: 'user_id,account_id' }
    )
    .select()
    .single();

  if (error) throw error;
  return rowToBalance(data);
}

// ── Net Worth Calculation ──────────────────────────────────────────────────────

export interface NetWorthSummary {
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
  totalCash: number;
  personalCash: number;
  businessCash: number;
  parkNLotBudget: number;
  totalDebt: number;
}

export function calculateNetWorth(accounts: AccountBalance[]): NetWorthSummary {
  // Assets: checking, savings, business, emergency, tax
  const assetTypes: AccountBalance['accountType'][] = ['checking', 'savings', 'business', 'emergency', 'tax'];
  // Liabilities: credit_card, loan
  const liabilityTypes: AccountBalance['accountType'][] = ['credit_card', 'loan'];

  const totalAssets = accounts
    .filter(a => assetTypes.includes(a.accountType))
    .reduce((s, a) => s + a.balance, 0);

  const totalLiabilities = accounts
    .filter(a => liabilityTypes.includes(a.accountType))
    .reduce((s, a) => s + a.balance, 0);

  const netWorth = totalAssets - totalLiabilities;

  const personalCash = accounts
    .filter(a => a.ownership === 'personal' && assetTypes.includes(a.accountType))
    .reduce((s, a) => s + a.balance, 0);

  const businessCash = accounts
    .filter(a => a.ownership === 'business' && assetTypes.includes(a.accountType))
    .reduce((s, a) => s + a.balance, 0);

  const totalCash = personalCash + businessCash;

  const totalDebt = totalLiabilities;

  const parkNLotBudget = accounts.find(a => a.accountId === 'parknlot')?.balance ?? 0;

  return {
    totalAssets,
    totalLiabilities,
    netWorth,
    totalCash,
    personalCash,
    businessCash,
    parkNLotBudget,
    totalDebt,
  };
}
